import { Form } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';

import { AppMessage } from '@/common/components';
import { FormFieldsErrors } from '@/common/interfaces';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { DnsRecordsQueryKeys, useAutocompleteDnsRecordsQuery } from '@/modules/DnsRecords';
import {
  DomainConfigFormFields,
  DomainConfigFormInputs,
  DomainsQueryKeys,
  useConfigureMultiDomainsMutation,
} from '@/modules/Domains';

import { DomainsConfigFormView } from './DomainConfigFormView';
import { domainConfigFormValidationSchema } from './validation';

interface Props {
  domainIds: string[];
  handleBack: () => void;
  handleCloseModal: () => void;
}

export const DomainConfigCreateMultiFormContainer: FC<Props> = memo(({ domainIds, handleBack, handleCloseModal }) => {
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<DomainConfigFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof DomainConfigFormInputs>>(null);

  const proxyServerIdFormValue = Form.useWatch<string>(DomainConfigFormInputs.ProxyServerId, form);
  const dnsRecordIdFormValue = Form.useWatch<string>(DomainConfigFormInputs.DnsRecordId, form);

  const { mutate: setupConfigMutation, isLoading: setupDeploymentConfigIsLoading } = useConfigureMultiDomainsMutation()({
    onSuccess: async () => {
      await queryClient.refetchQueries([DomainsQueryKeys.GetAll], { exact: false });

      handleCloseModal();
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof DomainConfigFormInputs>(err));
      messageOnError(err, appMessageApi);
    },
  });

  const initialValues: DomainConfigFormFields = {
    isHttps: false,
    siteId: undefined,
    dnsRecordId: undefined,
    proxyServerAddressId: undefined,
    proxyServerId: undefined,
    wwwRedirect: undefined,
    schemeRedirect: undefined,
    staticPrefix: undefined,
  };

  const handleFinish = useCallback(
    (values: DomainConfigFormFields): void => {
      setFormErrors(null);
      setupConfigMutation({ ...values, staticPrefix: values.staticPrefix || undefined, domainIds });
    },
    [domainIds],
  );

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  return (
    <DomainsConfigFormView
      formName="domain-configuration-setup-multi"
      formType="create"
      domainsCount={domainIds.length}
      form={form}
      formErrors={formErrors}
      initialValues={initialValues}
      isFormLoading={setupDeploymentConfigIsLoading}
      validationSchema={domainConfigFormValidationSchema}
      proxyServerIdFormValue={proxyServerIdFormValue}
      dnsRecordIdFormValue={dnsRecordIdFormValue}
      handleBack={handleBack}
      handleFinish={handleFinish}
      handleFinishFailed={handleFinishFailed}
    />
  );
});
